import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Button,
  Link as MuiLink,
  Stack,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  NodeSchedule,
  RichSession,
  UnscheduledAPISession,
  APISession,
} from "../../../redux/reducers/scheduleReducer";
import LaunchLinkSection from "../sessions/LaunchLinkSection";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { endSession } from "../../../redux/actions/sessionActions";
import { format } from "date-fns";
import { formatDistanceToNow } from "date-fns";
import { getSessionStatusColor, generateBetterstackLinks } from "./utils";
import SessionTimeline, { StateTransition } from "./SessionTimeline";
import BetterstackLinksSection from "./BetterstackLinksSection";

interface Props {
  node: NodeSchedule | null;
  session: RichSession | null;
  unscheduledSession: UnscheduledAPISession | null;
  nearlySchedulableSession?: APISession | null;
  onCloseNode: () => void;
  onCloseSession: () => void;
  onCloseUnscheduledSession: () => void;
  onCloseNearlySchedulableSession?: () => void;
}

const buildUrl = (id: string) => `/dashboard/builds/${id}`;
const ec2Url = (instanceId: string) =>
  `https://us-west-2.console.aws.amazon.com/ec2/home?region=us-west-2#InstanceDetails:instanceId=${instanceId}`;

/**
 * Format session age in seconds to a human-readable string
 */
const formatSessionAge = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(" ");
};

// Helper function to calculate timeline from state_history
const calculateTimeline = (stateHistory: any[]): StateTransition[] => {
  if (!stateHistory || stateHistory.length === 0) return [];

  const timeline: StateTransition[] = [];
  let prevTime: Date | null = null;
  let firstTime: Date | null = null;

  stateHistory.forEach((state) => {
    const enteredAt = new Date(state.entered_at);
    const timeDeltaMs = prevTime ? enteredAt.getTime() - prevTime.getTime() : 0;
    const absDeltaMs = firstTime ? enteredAt.getTime() - firstTime.getTime() : 0;

    // Extract metadata from state.data if available
    const metadata = state.data
      ? {
          scheduling_loop_id: state.data.scheduling_loop_id,
          log_session_id: state.data.log_session_id,
          finished_class: state.data.finished_class,
          node_id: state.data.node_id,
        }
      : undefined;

    timeline.push({
      status: state.data?.status || "unknown",
      entered_at: state.entered_at,
      time_delta_ms: timeDeltaMs,
      abs_delta_ms: absDeltaMs,
      metadata,
    });

    prevTime = enteredAt;
    if (!firstTime) firstTime = enteredAt;
  });

  return timeline;
};

const DetailPanel: React.FC<Props> = ({
  node,
  session,
  unscheduledSession,
  nearlySchedulableSession,
  onCloseNode,
  onCloseSession,
  onCloseUnscheduledSession,
  onCloseNearlySchedulableSession,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const activeItem = nearlySchedulableSession || unscheduledSession || session || node;

  // Calculate diagnostics data for sessions with state_history
  const activeSession = session?.s || unscheduledSession?.s || nearlySchedulableSession;
  const timeline = useMemo(() => {
    if (!activeSession?.state_history) return [];
    return calculateTimeline(activeSession.state_history);
  }, [activeSession?.state_history]);

  const betterstackLinks = useMemo(() => {
    if (!activeSession || !activeSession.state_history) return [];
    return generateBetterstackLinks(
      activeSession.session_id,
      activeSession.conversation_id,
      activeSession.state_history,
      activeSession.log_session_id || null
    );
  }, [activeSession]);
  const onClose = () => {
    onCloseNearlySchedulableSession?.();
    onCloseUnscheduledSession();
    onCloseSession();
    onCloseNode();
  };

  if (!activeItem) return null;

  const handleTerminate = async () => {
    const sessionId =
      session?.s?.session_id ||
      unscheduledSession?.s?.session_id ||
      nearlySchedulableSession?.session_id;

    if (!sessionId) return;
    try {
      setLoading(true);
      await dispatch(endSession(sessionId)).unwrap();
      setConfirmOpen(false);
      onClose(); // Close panel
    } catch (err) {
      console.error("Failed to terminate session:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#4a5568",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1.1rem", fontWeight: 500, color: "#ffffff" }}>
          {nearlySchedulableSession || unscheduledSession || session
            ? "Session Details"
            : "Node Details"}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#ffffff" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        {/* Unscheduled Session Details */}
        {unscheduledSession && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Session ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {unscheduledSession.s.session_id}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Connection Method
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {unscheduledSession.s.connection_method}
              </Typography>
            </Box>

            {/* User Email */}
            {unscheduledSession.s.user_email && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  User Email
                </Typography>
                <Typography variant="body1">{unscheduledSession.s.user_email}</Typography>
              </Box>
            )}

            {/* Session Status */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={unscheduledSession.s.status}
                size="small"
                sx={{
                  backgroundColor: getSessionStatusColor(unscheduledSession.s.status),
                  color: "#fff",
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Codec Telemetry */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Negotiated Codec
              </Typography>
              {unscheduledSession.s.telemetry?.negotiated_codec ? (
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {unscheduledSession.s.telemetry.negotiated_codec.codec}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Not available
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Codec Negotiation Duration
              </Typography>
              {unscheduledSession.s.telemetry?.negotiated_codec ? (
                <Typography variant="body2">
                  {unscheduledSession.s.telemetry.negotiated_codec.negotiation_duration_sec.toFixed(3)}s
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Not available
                </Typography>
              )}
            </Box>

            {/* Deployment ID */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Deployment ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {unscheduledSession.s.deployment_id}
              </Typography>
            </Box>

            {/* Organization ID */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Organization ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {unscheduledSession.s.organization_id}
              </Typography>
            </Box>

            {/* Launch Link */}
            {unscheduledSession.s.launch_args_id && (
              <LaunchLinkSection
                launchArgsId={unscheduledSession.s.launch_args_id}
                organizationId={unscheduledSession.s.organization_id}
                connectionMethod={unscheduledSession.s.connection_method}
              />
            )}

            {unscheduledSession.s.conversation_id && (
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    window.location.assign(
                      `/dashboard/conversations/${unscheduledSession.s.conversation_id}`
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  Open Conversation
                </Button>
              </Box>
            )}

            {/* Force Terminate Button */}
            <Box>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                {loading ? "Terminating..." : "Force Terminate Session"}
              </Button>
            </Box>

            {/* Created At */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created
              </Typography>
              <Typography variant="body2">
                {format(new Date(unscheduledSession.s.created_at), "yyyy-MM-dd HH:mm:ss")}
              </Typography>
            </Box>

            {/* Expire After */}
            {unscheduledSession.s.expire_after && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Expires
                </Typography>
                <Typography variant="body2">
                  {format(new Date(unscheduledSession.s.expire_after), "yyyy-MM-dd HH:mm:ss")}
                </Typography>
              </Box>
            )}

            {/* Rationale per Node */}
            {unscheduledSession.rationale_per_node &&
              Object.keys(unscheduledSession.rationale_per_node).length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    Scheduling Rationale
                  </Typography>
                  <Stack spacing={2}>
                    {Object.entries(unscheduledSession.rationale_per_node).map(
                      ([nodeId, reasons]) => (
                        <Box
                          key={nodeId}
                          sx={{
                            p: 2,
                            backgroundColor: "#f5f5f5",
                            borderRadius: 1,
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 500,
                              mb: 1,
                              color: "#2d3748",
                            }}
                          >
                            {nodeId}
                          </Typography>
                          <Stack spacing={0.5}>
                            {reasons.map((reason, idx) => (
                              <Typography
                                key={idx}
                                variant="body2"
                                sx={{
                                  color: "#e53e3e",
                                  fontSize: "0.875rem",
                                  pl: 2,
                                  "&:before": {
                                    content: '"• "',
                                    position: "absolute",
                                    marginLeft: "-1.2em",
                                  },
                                }}
                              >
                                {reason}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )
                    )}
                  </Stack>
                </Box>
              )}

            {/* Session Diagnostics - Timeline and Betterstack Links */}
            {timeline.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <SessionTimeline
                  timeline={timeline}
                  sessionId={unscheduledSession.s.session_id}
                />
                {betterstackLinks.length > 0 && (
                  <BetterstackLinksSection links={betterstackLinks} />
                )}
              </Box>
            )}
          </Stack>
        )}

        {/* Nearly Schedulable Session Details */}
        {nearlySchedulableSession && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Session ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {nearlySchedulableSession.session_id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Connection Method
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {nearlySchedulableSession.connection_method}
              </Typography>
            </Box>

            {/* User Email */}
            {nearlySchedulableSession.user_email && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  User Email
                </Typography>
                <Typography variant="body1">{nearlySchedulableSession.user_email}</Typography>
              </Box>
            )}

            {/* Session Status */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip
                label={nearlySchedulableSession.status}
                size="small"
                sx={{
                  backgroundColor: getSessionStatusColor(nearlySchedulableSession.status),
                  color: "#fff",
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Codec Telemetry */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Negotiated Codec
              </Typography>
              {nearlySchedulableSession.telemetry?.negotiated_codec ? (
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {nearlySchedulableSession.telemetry.negotiated_codec.codec}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Not available
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Codec Negotiation Duration
              </Typography>
              {nearlySchedulableSession.telemetry?.negotiated_codec ? (
                <Typography variant="body2">
                  {nearlySchedulableSession.telemetry.negotiated_codec.negotiation_duration_sec.toFixed(3)}s
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                  Not available
                </Typography>
              )}
            </Box>

            {/* Deployment ID */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Deployment ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {nearlySchedulableSession.deployment_id}
              </Typography>
            </Box>

            {/* Organization ID */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Organization ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {nearlySchedulableSession.organization_id}
              </Typography>
            </Box>

            {/* Launch Link */}
            {nearlySchedulableSession.launch_args_id && (
              <LaunchLinkSection
                launchArgsId={nearlySchedulableSession.launch_args_id}
                organizationId={nearlySchedulableSession.organization_id}
                connectionMethod={nearlySchedulableSession.connection_method}
              />
            )}

            {nearlySchedulableSession.conversation_id && (
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    window.location.assign(
                      `/dashboard/conversations/${nearlySchedulableSession.conversation_id}`
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  Open Conversation
                </Button>
              </Box>
            )}

            {/* Force Terminate Button */}
            <Box>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                {loading ? "Terminating..." : "Force Terminate Session"}
              </Button>
            </Box>

            {/* Created At */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created
              </Typography>
              <Typography variant="body2">
                {format(new Date(nearlySchedulableSession.created_at), "yyyy-MM-dd HH:mm:ss")}
              </Typography>
            </Box>

            {/* Expire After */}
            {nearlySchedulableSession.expire_after && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Expires
                </Typography>
                <Typography variant="body2">
                  {format(new Date(nearlySchedulableSession.expire_after), "yyyy-MM-dd HH:mm:ss")}
                </Typography>
              </Box>
            )}

            {/* Nearly Schedulable Notice */}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This session is nearly schedulable. It may require minor adjustments or waiting for
                resources to become available before it can be scheduled.
              </Typography>
            </Alert>

            {/* Session Diagnostics - Timeline and Betterstack Links */}
            {timeline.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <SessionTimeline
                  timeline={timeline}
                  sessionId={nearlySchedulableSession.session_id}
                />
                {betterstackLinks.length > 0 && (
                  <BetterstackLinksSection links={betterstackLinks} />
                )}
              </Box>
            )}
          </Stack>
        )}

        {/* Node Details */}
        {node && !session && !unscheduledSession && !nearlySchedulableSession && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Node ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {node.node_id}
              </Typography>
            </Box>

            {node.node_type === "AWS_ASG_INSTANCE" && node.node_id.startsWith("asg::") && (
              <Box>
                <MuiLink
                  href={ec2Url(node.node_id.replace("asg::", ""))}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                >
                  Open in AWS Console ↗
                </MuiLink>
              </Box>
            )}

            {node.node_idle_s != null && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Idle Time
                </Typography>
                <Typography variant="body1">
                  {formatDistanceToNow(Date.now() - node.node_idle_s * 1000)}
                </Typography>
              </Box>
            )}
            {node.available_build_ids && node.available_build_ids.length > 0 && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Available Builds
                </Typography>
                <Stack spacing={1}>
                  {node.available_build_ids.map((buildId, idx) => (
                    <MuiLink
                      key={idx}
                      href={buildUrl(buildId)}
                      underline="hover"
                      target="_blank"
                      rel="noreferrer"
                      sx={{ fontSize: "0.875rem", display: "block" }}
                    >
                      build:{buildId.slice(0, 8)}
                    </MuiLink>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sessions ({node.sessions.length})
              </Typography>
              <Stack spacing={1}>
                {node.sessions.map((s, idx) => {
                  const buildId = s.is_phantom ? s.phantom_build_id : s.build_id;
                  return (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={s.is_phantom ? "phantom" : s.s?.session_id?.slice(0, 8)}
                        size="small"
                        variant={s.is_phantom ? "outlined" : "filled"}
                        sx={s.is_phantom ? { borderStyle: "dashed" } : {}}
                      />
                      {buildId && (
                        <MuiLink
                          href={buildUrl(buildId)}
                          underline="hover"
                          target="_blank"
                          rel="noreferrer"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          build:{buildId.slice(0, 8)}
                        </MuiLink>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}

        {/* Session Details */}
        {session && !unscheduledSession && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Session ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {session.session_id || session.s?.session_id || "Unknown"}
              </Typography>
              {session.is_phantom && (
                <Chip
                  label="Phantom"
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1, borderStyle: "dashed" }}
                />
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Connection Method
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                {session.is_phantom ? "N/A (Phantom)" : session.s?.connection_method}
              </Typography>
            </Box>
            {/* User Email */}
            {session.s?.user_email && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  User Email
                </Typography>
                <Typography variant="body1">{session.s.user_email}</Typography>
              </Box>
            )}

            {/* Session Status */}
            {session.s?.status && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={session.s.status}
                  size="small"
                  sx={{
                    backgroundColor: getSessionStatusColor(session.s.status),
                    color: "#fff",
                    fontWeight: 500,
                  }}
                />
              </Box>
            )}

            {/* Codec Telemetry */}
            {session.s && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Negotiated Codec
                  </Typography>
                  {session.s.telemetry?.negotiated_codec ? (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {session.s.telemetry.negotiated_codec.codec}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Not available
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Codec Negotiation Duration
                  </Typography>
                  {session.s.telemetry?.negotiated_codec ? (
                    <Typography variant="body2">
                      {session.s.telemetry.negotiated_codec.negotiation_duration_sec.toFixed(3)}s
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Not available
                    </Typography>
                  )}
                </Box>
              </>
            )}

            {node &&
              node.node_type === "AWS_ASG_INSTANCE" &&
              node.node_id.startsWith("asg::") && (
                <Box>
                  <MuiLink
                    href={ec2Url(node.node_id.replace("asg::", ""))}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                  >
                    Open node in AWS Console ↗
                  </MuiLink>
                </Box>
              )}

            {(session.is_phantom ? session.phantom_build_id : session.build_id) && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Build
                </Typography>
                <MuiLink
                  href={buildUrl(
                    (session.is_phantom ? session.phantom_build_id : session.build_id)!
                  )}
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                >
                  {session.is_phantom ? session.phantom_build_id : session.build_id}
                </MuiLink>
              </Box>
            )}

            {session.s?.conversation_id && (
              <Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    window.location.assign(`/dashboard/conversations/${session.s!.conversation_id}`)
                  }
                  sx={{ textTransform: "none" }}
                >
                  Open Conversation
                </Button>
              </Box>
            )}

            {/* Launch Link */}
            {session.s?.launch_args_id && session.s?.organization_id && (
              <LaunchLinkSection
                launchArgsId={session.s.launch_args_id}
                organizationId={session.s.organization_id}
                connectionMethod={session.s.connection_method}
              />
            )}

            {/* Force Terminate Button - only if not phantom */}
            {!session.is_phantom && (
              <Box>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={() => setConfirmOpen(true)}
                  disabled={loading}
                  sx={{ textTransform: "none" }}
                >
                  {loading ? "Terminating..." : "Force Terminate Session"}
                </Button>
              </Box>
            )}

            {session.s?.created_at && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">
                  {format(new Date(session.s.created_at), "yyyy-MM-dd HH:mm:ss")}
                </Typography>
              </Box>
            )}

            {/* Phantom Session Lifetime Info */}
            {session.launch_time && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Launch Time
                </Typography>
                <Typography variant="body2">
                  {format(new Date(session.launch_time), "yyyy-MM-dd HH:mm:ss")}
                </Typography>
              </Box>
            )}

            {session.launch_time && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Session Lifetime
                </Typography>
                <Typography variant="body2">
                  {formatSessionAge(
                    (new Date().getTime() - new Date(session.launch_time).getTime()) / 1000
                  )}
                </Typography>
              </Box>
            )}

            {/* Launch Arguments */}
            {session.launch_args && session.launch_args.length > 0 && (
              <Box>
                <Accordion
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #e0e0e0",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: "#f5f5f5",
                      minHeight: "48px",
                      "&.Mui-expanded": { minHeight: "48px" },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      Launch Arguments ({session.launch_args.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, backgroundColor: "#fafafa" }}>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        margin: 0,
                        padding: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        maxHeight: "400px",
                        overflowY: "auto",
                      }}
                    >
                      {session.launch_args.join("\n")}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}

            {/* Session Diagnostics - Timeline and Betterstack Links */}
            {timeline.length > 0 && (
              <Box>
                <Divider sx={{ my: 2 }} />
                <SessionTimeline
                  timeline={timeline}
                  sessionId={session.s?.session_id || ""}
                />
                {betterstackLinks.length > 0 && (
                  <BetterstackLinksSection links={betterstackLinks} />
                )}
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Termination</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to force terminate this session? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleTerminate} disabled={loading}>
            {loading ? "Terminating..." : "Terminate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DetailPanel;
